import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import type { HomeReminder } from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { ReminderControls } from '../../ui/ReminderControls';
import { sx } from '../../ui/designStyles';
import type { MemberCtx } from './MemberHome';
import { agoText, callFirstOf, careWorkerOf, deviceLabel, givenName, meFirstName, TONE } from './memberData';
import { MyDayBlock } from './MyDayBlock';
import { HomeCalm } from './HomeCalm';
import { FirstTalk } from '../companion/FirstTalk';
import { facts, steps } from './Sheet';
import { Act, chipC, D, DASH, S } from './ui';
import { BuildLabel, useBuildLabels, useCustomerText } from '../../ui/buildLabels';

// Today (design T11060-11244, homeVals hTabToday). Status, ring and devices read the
// real GET /athome/me/:id; Coming up reads the real reminders; "Atlas noticed"
// reads the caregiving dashboard's highlights; the check-in and the week's readings wait
// on the PROPOSED GET /athome/me/:id/today.

const SECTION = 'font-size:16px;font-weight:700;letter-spacing:.12em;text-transform:uppercase;color:#22272E;';
const ROW_LINK = 'display:flex;align-items:center;gap:12px;min-height:56px;padding:11px 8px;margin:0 -8px;border-radius:9px;border-top:1px solid rgba(34,39,46,.08);text-decoration:none;color:inherit;transition:background .16s ease,padding-left .16s cubic-bezier(.22,.61,.36,1);';

const DAY = 86_400_000;
const WEEKDAY = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTH = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** "Friday", "Next Tuesday", "4 November": when a reminder next fires, as the design words it. */
export function whenLabel(iso: string, now = new Date()): string {
  const t = new Date(iso);
  const a = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const b = new Date(t.getFullYear(), t.getMonth(), t.getDate()).getTime();
  const days = Math.round((b - a) / DAY);
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  if (days > 1 && days < 7) return WEEKDAY[t.getDay()];
  if (days >= 7 && days < 14) return `Next ${WEEKDAY[t.getDay()]}`;
  return `${t.getDate()} ${MONTH[t.getMonth()]}`;
}

const clockOf = (iso: string) => new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

export function TodayTab({ c, toast }: { c: MemberCtx; toast: ReactNode }) {
  const plain = useCustomerText();
  const buildLabels = useBuildLabels();
  const navigate = useNavigate();
  const { client } = useSession();
  const { d } = c;
  const me = d.me.data;
  const people = d.people.data;
  const today = d.today.data;
  const name = meFirstName(me);
  const callFirst = callFirstOf(people, me?.primaryContact ?? null);
  const careWorker = careWorkerOf(people);
  const openItems = me?.openCareItems.value ?? 0;
  const hrs = new Date().getHours();
  const night = hrs >= 19 || hrs < 6;
  const ago = me?.lastCheckInMinutes == null ? null : agoText(me.lastCheckInMinutes);
  const chats = me?.conversationsThisWeek.value;
  const ready = me?.readiness.value ?? null;
  const dash = (r: number) => `${Math.round(2 * Math.PI * r * (ready ?? 0) / 100 * 10) / 10} ${Math.round(2 * Math.PI * r * 10) / 10}`;
  const daily = today?.daily ?? null;
  // While the first talk is on, the daily check-in waits (design roSuHold).
  const talking = !!c.firstTalk.data?.show;

  const sendDaily = (answer: 'yes' | 'no' | null, msg: string | null) => {
    if (c.readOnly) return;
    c.act({ kind: 'daily', value: answer })
      .then(() => {
        c.setToast(msg);
        d.today.reload();
      })
      .catch((e: Error) => c.setToast(`Not sent: ${e.message}.`));
  };

  const devices = (me?.devices ?? []).map((v) => {
    const label = deviceLabel(v.provider);
    return { label, ok: v.reporting, note: v.lastSeenMinutes == null ? 'never reported' : `seen ${agoText(v.lastSeenMinutes)}` };
  });

  const contact = me?.primaryContact ?? null;
  const contactFirst = contact ? givenName(contact) : null;
  const quick = [
    {
      label: contactFirst ? `Message ${contactFirst}` : 'Message your care team',
      note: today?.contactNote ?? `goes to ${contact ?? 'the Aithora care line'}`,
      glyph: '✉',
      color: '#2F5C97',
      sheet: {
        kicker: 'MESSAGE',
        title: `Send ${contactFirst ?? 'your care team'} a note`,
        body: `Type it or just talk. The tablet will write it down. ${contactFirst ?? 'The care line'} gets it on ${contactFirst ? 'her' : 'their'} phone straight away.`,
        input: 'Hi love, all fine here today…',
        primary: `Send to ${contactFirst ?? 'the care line'}`,
        onPrimary: (text: string) => c.act({ kind: 'message', target: contact, note: text }),
      },
    },
  ];

  const upcoming = (d.reminders.data?.reminders ?? [])
    .filter((r: HomeReminder) => r.status === 'active' && r.nextFireAt && r.kind !== 'medication')
    .sort((a, b) => Date.parse(a.nextFireAt!) - Date.parse(b.nextFireAt!))
    .slice(0, 3);
  const highlights = d.dashboard.data?.companionHighlights.highlights ?? [];

  return (
    <>
      <HomeCalm c={c} />
      <div style={sx('margin-bottom:12px;min-width:0;')}>
        <FirstTalk residentId={c.personId} fullName={me?.name ?? ''} q={c.firstTalk} onBigger={c.bigger} home readOnlyReason={c.readOnly ? (c.reason ?? 'Read only') : null} label="Home first talk" />
      </div>
      <D css="display:grid;grid-template-columns:repeat(auto-fit,minmax(330px,1fr));gap:12px;min-width:0;align-items:start;">
        <D css="grid-column:1/-1;min-width:0;animation:riseIn .46s cubic-bezier(.22,.61,.36,1);">
          <S css="display:block;font-size:16px;font-weight:700;letter-spacing:.14em;color:#54606C;">
            {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }).toUpperCase()}
          </S>
          {!talking && daily && daily.answer === null && (
            <S css="display:block;box-sizing:border-box;margin-top:10px;padding:20px 22px;border-radius:18px;background:rgba(47,92,151,.09);border:1px solid rgba(47,92,151,.32);min-width:0;">
              <S css="display:block;font-size:28px;font-weight:700;letter-spacing:-.025em;line-height:1.14;color:#22272E;text-wrap:pretty;">{`How are you today, ${name}?`}</S>
              <S css="display:block;font-size:17px;color:#3C454F;line-height:1.5;margin-top:7px;text-wrap:pretty;">
                One tap. It tells everyone at once, and it is how Atlas learns what normal looks like for you.
              </S>
              <S css="display:flex;gap:10px;flex-wrap:wrap;margin-top:14px;min-width:0;">
                <Act css="flex:2 1 190px;min-width:0;display:inline-flex;align-items:center;justify-content:center;min-height:64px;padding:0 22px;border-radius:15px;font-size:21px;font-weight:700;text-decoration:none;color:#FCFAF4;background:#1F6B52;transition:background .16s ease;" onClick={() => sendDaily('yes', `Thank you. ${callFirst.charAt(0).toUpperCase()}${callFirst.slice(1)} has been told you are well today.`)} disabled={c.readOnly} reason={c.reason}>
                  Yes, I am fine
                </Act>
                <Act css="flex:1 1 150px;min-width:0;display:inline-flex;align-items:center;justify-content:center;min-height:64px;padding:0 18px;border-radius:15px;font-size:19px;font-weight:700;text-decoration:none;color:#4A5866;background:#FCFAF4;border:1px solid rgba(91,107,124,.5);transition:background .16s ease;" onClick={() => sendDaily('no', 'Someone will call you within the hour. You do not need to do anything else.')} disabled={c.readOnly} reason={c.reason}>
                  Not really
                </Act>
              </S>
            </S>
          )}
          {!talking && daily && daily.answer !== null && (
            <S css="display:flex;align-items:center;gap:14px;flex-wrap:wrap;row-gap:8px;box-sizing:border-box;margin-top:10px;padding:14px 18px;border-radius:16px;background:rgba(47,138,111,.11);border:1px solid rgba(47,138,111,.35);min-width:0;animation:riseIn .3s cubic-bezier(.22,.61,.36,1);">
              <S css="flex:0 0 auto;display:inline-flex;align-items:center;justify-content:center;width:34px;height:34px;border-radius:11px;font-size:19px;font-weight:700;color:#FCFAF4;background:#1F6B52;">{'✓'}</S>
              <S css="flex:1 1 200px;min-width:0;">
                <S css="display:block;font-size:18px;font-weight:700;color:#12503C;text-wrap:pretty;">
                  {daily.answer === 'no' ? 'You said you are not feeling right. Someone is calling you.' : 'You checked in today. Nobody needs to phone to make sure.'}
                </S>
                <S css="display:block;font-size:16px;color:#3C454F;margin-top:2px;">
                  <b style={sx('font-weight:700;')}>{`${daily.streakDays} days`}</b> in a row. Atlas only knows something is wrong because it knows what your normal is.
                </S>
              </S>
              <Act css="flex:0 0 auto;display:inline-flex;align-items:center;min-height:44px;padding:0 16px;border-radius:12px;font-size:16px;font-weight:700;text-decoration:none;color:#3C454F;background:#FCFAF4;border:1px solid rgba(34,39,46,.2);" onClick={() => sendDaily(null, null)} disabled={c.readOnly} reason={c.reason}>
                Change it
              </Act>
            </S>
          )}
          <S css="display:flex;align-items:flex-start;gap:22px;flex-wrap:wrap;row-gap:14px;min-width:0;margin-top:8px;">
            <S css="flex:1 1 300px;min-width:0;">
              <S css="display:block;font-size:26px;font-weight:700;letter-spacing:-.02em;line-height:1.18;color:#22272E;text-wrap:pretty;">
                {!me ? DASH : openItems ? `Something needs a look, ${name}.` : night ? `You’re set for tonight, ${name}.` : `All quiet this morning, ${name}.`}
              </S>
              <S css="display:block;font-size:17px;line-height:1.5;margin-top:9px;color:#3C454F;text-wrap:pretty;">
                {me
                  ? `${ago ? `Last check-in ${ago}` : 'No check-in heard yet'} · ${openItems ? `${openItems}${openItems === 1 ? ' thing the care team is looking at' : ' things the care team is looking at'}` : 'nothing open with the care team'} · ${chats == null ? DASH + ' conversations' : chats === 1 ? '1 conversation' : chats + ' conversations'} this week.`
                  : d.me.error
                    ? `Your Home did not load: ${d.me.error.message}`
                    : ''}
              </S>
            </S>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                c.goTab('home');
              }}
              title="How ready your home is"
              style={sx('flex:0 0 auto;display:flex;align-items:center;gap:13px;min-height:56px;padding:8px 16px 8px 10px;border:1px solid rgba(34,39,46,.2);border-radius:14px;text-decoration:none;color:#22272E;transition:background .16s ease;')}
            >
              <svg viewBox="0 0 56 56" width="56" height="56" style={sx('display:block;flex:0 0 56px;transform:rotate(-90deg);')} aria-hidden="true">
                <circle cx="28" cy="28" r="24" fill="none" stroke="rgba(34,39,46,.14)" strokeWidth="7" />
                <circle cx="28" cy="28" r="24" fill="none" stroke="#2F5C97" strokeWidth="7" strokeDasharray={dash(24)} />
              </svg>
              <S css="display:block;">
                <S css="display:block;font-size:26px;font-weight:700;letter-spacing:-.03em;line-height:1;">{ready == null ? DASH : `${Math.round(ready)}%`}</S>
                <S css="display:block;font-size:16px;font-weight:700;letter-spacing:.1em;color:#54606C;margin-top:4px;">HOME READY</S>
              </S>
            </a>
          </S>
          <S css="display:flex;gap:10px;flex-wrap:wrap;margin-top:16px;min-width:0;">
            {devices.map((v, i) => (
              <Act
                key={i}
                css="flex:1 1 190px;min-width:0;display:inline-flex;align-items:center;gap:11px;min-height:56px;padding:8px 14px;border-radius:14px;text-decoration:none;color:#22272E;border-top:1px solid rgba(34,39,46,.14);transition:background .16s ease;"
                onClick={() =>
                  c.openSheet({
                    kicker: 'DEVICE',
                    title: `${v.label} · ${v.ok ? 'working' : 'needs a look'}`,
                    glyph: v.ok ? '✓' : '!',
                    color: v.ok ? '#12503C' : '#4A5866',
                    body:
                      v.label === 'Watch'
                        ? 'Detects a fall and asks if you are alright. If you do not answer in 60 seconds it starts the ladder.'
                        : v.label === 'Pendant'
                          ? 'Press and hold for two seconds to call for help from anywhere in the house or garden.'
                          : 'Atlas lives here. It also shows who is coming today and reads out reminders.',
                    rows: steps([
                      ['Paired', v.note],
                      ['Last test', v.label === 'Tablet' ? 'checks in every 15 minutes' : 'no test on record yet'],
                      ['If it goes quiet', `${callFirst.charAt(0).toUpperCase()}${callFirst.slice(1)} is told within the hour, and the care line checks the connection`],
                    ]),
                    primary: 'Test it now',
                    onPrimary: () => c.act({ kind: 'device_test', target: v.label }),
                  })
                }
              >
                <S css={'flex:0 0 30px;display:inline-flex;align-items:center;justify-content:center;width:30px;height:30px;border-radius:9px;font-size:17px;font-weight:700;color:#FCFAF4;background:' + (v.ok ? '#1F6B52' : '#4A5866') + ';'}>{v.ok ? '✓' : '!'}</S>
                <S css="min-width:0;">
                  <S css="display:block;font-size:16px;font-weight:700;color:#22272E;">{v.label}</S>
                  <S css={'display:block;font-size:16px;color:' + (v.ok ? '#1F6B52' : '#4A5866') + ';font-weight:700;'}>{v.ok ? 'Working' : 'Needs a look'}</S>
                </S>
              </Act>
            ))}
          </S>
        </D>

        <D css="grid-column:1/-1;display:grid;grid-template-columns:repeat(auto-fit,minmax(190px,1fr));gap:10px;min-width:0;animation:riseIn .46s cubic-bezier(.22,.61,.36,1);animation-delay:60ms;">
          {quick.map((q) => (
            <Act
              key={q.label}
              css="display:block;box-sizing:border-box;min-height:134px;padding:16px 16px 15px;border-radius:16px;text-decoration:none;color:#22272E;background:#FCFAF4;border:1px solid rgba(34,39,46,.14);border-radius:10px;box-shadow:0 1px 2px rgba(34,39,46,.06);transition:border-color .16s ease,background .16s ease;"
              onClick={() => c.openSheet(q.sheet)}
            >
              <S css={'display:inline-flex;align-items:center;justify-content:center;width:44px;height:44px;border-radius:12px;font-size:21px;font-weight:700;color:#FCFAF4;background:' + q.color + ';'}>{q.glyph}</S>
              <S css="display:block;font-size:22px;font-weight:700;letter-spacing:-.015em;margin-top:13px;">{q.label}</S>
              <S css="display:block;font-size:16px;color:#54606C;margin-top:4px;line-height:1.4;">{q.note}</S>
            </Act>
          ))}
        </D>
        {/* "I need a hand" and "Atlas" are the two big buttons at the top (HomeCalm): one of each. */}
        <MyDayBlock personId={c.personId} readOnly={c.readOnly} reason={c.reason} />
        {toast}

        <D css="grid-column:1/-1;box-sizing:border-box;min-width:0;padding-top:14px;border-top:1px solid rgba(34,39,46,.16);">
          <D css="display:flex;align-items:baseline;gap:10px;flex-wrap:wrap;padding-bottom:2px;">
            <S css="flex:0 0 auto;font-size:16px;font-weight:700;letter-spacing:.12em;text-transform:uppercase;white-space:nowrap;color:#22272E;">What your week is telling us</S>
            <BuildLabel><S css={chipC('#5B6B7C', 'rgba(91,107,124,.14)')}>MODELED</S></BuildLabel>
          </D>
          {(today?.week ?? []).map((r, i) => {
            const col = TONE[r.tone];
            return (
              <D key={i} css={'display:flex;align-items:flex-start;gap:12px;min-width:0;padding:13px 0;border-top:1px solid rgba(34,39,46,.1);animation:riseIn .46s cubic-bezier(.22,.61,.36,1);animation-delay:' + i * 60 + 'ms;'}>
                <S css={'flex:0 0 10px;width:10px;height:10px;border-radius:50%;margin-top:7px;background:' + col + ';'} />
                <S css="flex:1 1 240px;min-width:0;">
                  <S css="display:block;font-size:19px;font-weight:700;letter-spacing:-.01em;color:#22272E;text-wrap:pretty;">{r.title}</S>
                  <S css="display:block;font-size:16px;color:#3C454F;line-height:1.45;margin-top:3px;text-wrap:pretty;">{r.observation}</S>
                  <S css={'display:block;font-size:16px;font-weight:700;line-height:1.45;margin-top:5px;text-wrap:pretty;color:' + col + ';'}>{r.meaning}</S>
                </S>
                <Act
                  css={'flex:0 0 auto;display:inline-flex;align-items:center;min-height:48px;padding:0 18px;border-radius:13px;font-size:16px;font-weight:700;text-decoration:none;color:#FCFAF4;background:' + col + ';'}
                  onClick={() =>
                    c.openSheet({
                      kicker: 'WHAT WE NOTICED',
                      title: r.title,
                      glyph: '↗',
                      color: col,
                      body: `${r.observation} ${r.meaning}`,
                      rows: facts([
                        ['Where this comes from', 'your own last 90 days, not an average'],
                        ['Confidence', 'MODELED, medium'],
                        ['If you disagree', 'say so and Atlas stops flagging it'],
                      ]),
                      primary: r.action,
                      onPrimary: () => c.act({ kind: 'reminder', target: r.title, note: r.action }),
                    })
                  }
                >
                  {r.action}
                </Act>
              </D>
            );
          })}
          <D css="font-size:16px;color:#54606C;line-height:1.5;margin-top:10px;text-wrap:pretty;">
            {today
              ? (buildLabels ? 'MODELED from your own last 90 days' : 'Worked out from your own last 90 days') + ', not from an average of other people. We say what we think it means, and we show you why. If we are wrong, tell Atlas and it stops.'
              : plain('Nothing to read yet. The week’s readings wait on the member feed (GET /athome/me/:id/today), and we would rather show nothing than guess.')}
          </D>
        </D>

        <D css="box-sizing:border-box;min-width:0;padding-top:14px;border-top:1px solid rgba(34,39,46,.16);animation:riseIn .46s cubic-bezier(.22,.61,.36,1);animation-delay:180ms;">
          <D css="display:flex;align-items:baseline;gap:10px;flex-wrap:wrap;">
            <S css={'flex:1 1 0;min-width:0;' + SECTION}>Coming up</S>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                c.goTab('people');
              }}
              style={sx('font-size:16px;color:#2F5C97;text-decoration:none;font-weight:700;')}
            >
              Your people {'→'}
            </a>
          </D>
          {/* With no appointment on file the design says so, under whatever else is coming up. */}
          {[...upcoming, ...(upcoming.some((r) => r.kind === 'appointment') ? [] : [null])].map((r, i) => {
            const when = r ? whenLabel(r.nextFireAt!) : DASH;
            const what = r ? r.title : 'No appointments on file';
            return (
              <Act
                key={r?.id ?? i}
                css={ROW_LINK}
                onClick={() =>
                  c.openSheet({
                    kicker: `COMING UP · ${when.toUpperCase()}`,
                    title: what,
                    glyph: '○',
                    color: '#2F5C97',
                    body: r ? r.body ?? '' : `Nothing has been booked into ${name}’s calendar yet. Appointments appear here once the care team adds them.`,
                    rows: steps(
                      r
                        ? [['When', `${whenLabel(r.nextFireAt!)}, ${clockOf(r.nextFireAt!)}`], ['How', r.channel === 'voice' ? 'Atlas says it out loud' : 'a reminder on the tablet'], ['Then', 'it moves to the next time']]
                        : [['On file', 'nothing yet'], ['Who can add one', careWorker ?? 'the Aithora care line']],
                    ),
                    primary: 'Remind me the morning of',
                    onPrimary: () => c.act({ kind: 'reminder', target: r?.id ?? null, note: 'morning of' }),
                  })
                }
              >
                <S css="flex:0 0 96px;font-size:16px;font-weight:700;color:#54606C;">{when}</S>
                <S css="flex:1 1 0;min-width:0;font-size:17px;color:#22272E;line-height:1.4;">{what}</S>
                <S css="font-size:20px;font-weight:700;color:#54606C;">{'›'}</S>
              </Act>
            );
          })}
          {/* Restored from production (not in the design): add, pause, resume and cancel reminders. */}
          <ReminderControls
            client={client}
            residentId={c.personId}
            reminders={(d.reminders.data?.reminders ?? []).map((r: HomeReminder) => ({ id: String(r.id), title: r.title, kind: String(r.kind), status: String(r.status) }))}
            readOnly={c.readOnly}
            readOnlyReason={c.reason}
            onChanged={() => d.reminders.reload()}
            big
          />
        </D>

        <D css="box-sizing:border-box;min-width:0;padding-top:14px;border-top:1px solid rgba(34,39,46,.16);animation:riseIn .46s cubic-bezier(.22,.61,.36,1);animation-delay:240ms;">
          <D css={SECTION}>Atlas noticed</D>
          {(highlights.length ? highlights : [null]).map((h, i) => {
            const meta = today?.noticed[i] ?? null;
            // The empty line agrees with the header's count of conversations: talked this week
            // with nothing kept to remember is not "no conversations recorded".
            const text = h ?? (chats ? `Nothing from this week’s ${chats === 1 ? 'conversation' : 'conversations'} was kept to remember yet.` : `No Atlas conversations have been recorded for ${name} yet.`);
            const tag = h ? meta?.tag ?? '' : '';
            return (
              <Act
                key={i}
                css="display:flex;gap:12px;min-height:56px;padding:11px 8px;margin:0 -8px;border-radius:9px;border-top:1px solid rgba(34,39,46,.08);text-decoration:none;color:inherit;transition:background .16s ease,padding-left .16s cubic-bezier(.22,.61,.36,1);"
                onClick={() =>
                  c.openSheet({
                    kicker: 'ATLAS NOTICED',
                    title: text,
                    glyph: '“',
                    color: '#5B6B7C',
                    body: `Filed as: ${tag || 'a highlight'}. Only what you agreed to share is kept; the words themselves are not.`,
                    rows: facts([
                      ['Who can see this', `You, ${callFirst}${careWorker ? `, and ${careWorker}` : ''}`],
                      ['Kept for', '90 days, then summarised'],
                      ['Wrong?', 'Tell Atlas and it is removed'],
                    ]),
                    primary: 'Open in Atlas',
                    onPrimary: async () => {
                      navigate('/me');
                      return null;
                    },
                  })
                }
              >
                <S css="flex:0 0 96px;font-size:16px;font-weight:700;color:#54606C;">{h ? meta?.when ?? '' : DASH}</S>
                <S css="flex:1 1 0;min-width:0;">
                  <S css="display:block;font-size:17px;color:#22272E;line-height:1.45;">{text}</S>
                  <S css="display:block;font-size:16px;color:#54606C;margin-top:4px;">{tag}</S>
                </S>
                <S css="font-size:20px;font-weight:700;color:#54606C;align-self:center;">{'›'}</S>
              </Act>
            );
          })}
        </D>
      </D>
    </>
  );
}
